import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, 
  Heart, 
  Star, 
  Trophy, 
  Users,
  Mail,
  Play,
  Pause
} from 'lucide-react';

const SponsorshipPage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = React.useRef(null);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="w-6 h-6 text-primary" />
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">Support Our Youth</span>
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight uppercase text-foreground leading-none mb-6">
            Elevated Youth Program
          </h1>
          <p className="font-serif text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8">
            Help us build the next generation of champions. Your sponsorship directly supports young ropers 
            pursuing their dreams in the arena.
          </p>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-8 md:py-12 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video
              ref={videoRef}
              className="w-full aspect-video"
              poster=""
              controls
              playsInline
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              data-testid="sponsorship-video"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_c4c7d8c3-163d-4839-9ffd-daa88c46739c/artifacts/3o4hl1yb_Elevated%20Youth%20Program%20%281%29.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-4">
            Watch how your support makes a difference
          </p>
        </div>
      </section>

      {/* About the Program */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tight mb-6">
                Why Sponsor Our Youth Athletes?
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="text-lg">
                  The Elevated Youth Program is dedicated to nurturing young talent in the roping community. 
                  We believe every young athlete deserves the opportunity to learn from the best, compete 
                  at the highest levels, and develop the skills that will serve them throughout their lives.
                </p>
                <p>
                  Your sponsorship helps cover coaching fees, competition entry costs, equipment, and travel 
                  expenses for youth athletes who might otherwise not have access to these opportunities.
                </p>
                <p>
                  Beyond the arena, we're building character, teaching discipline, and creating a supportive 
                  community where young ropers can thrive. Many of our youth athletes come from ranching 
                  families who work hard to provide these experiences, and your support makes all the difference.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-6 text-center">
                  <Star className="w-10 h-10 text-primary mx-auto mb-3" />
                  <h3 className="font-display font-bold text-2xl mb-1">50+</h3>
                  <p className="text-sm text-muted-foreground">Youth Athletes Supported</p>
                </CardContent>
              </Card>
              <Card className="bg-secondary/5 border-secondary/20">
                <CardContent className="p-6 text-center">
                  <Trophy className="w-10 h-10 text-secondary mx-auto mb-3" />
                  <h3 className="font-display font-bold text-2xl mb-1">100+</h3>
                  <p className="text-sm text-muted-foreground">Competitions Entered</p>
                </CardContent>
              </Card>
              <Card className="bg-accent/5 border-accent/20">
                <CardContent className="p-6 text-center">
                  <Users className="w-10 h-10 text-accent mx-auto mb-3" />
                  <h3 className="font-display font-bold text-2xl mb-1">3</h3>
                  <p className="text-sm text-muted-foreground">Professional Coaches</p>
                </CardContent>
              </Card>
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-6 text-center">
                  <Heart className="w-10 h-10 text-primary mx-auto mb-3" />
                  <h3 className="font-display font-bold text-2xl mb-1">100%</h3>
                  <p className="text-sm text-muted-foreground">Goes to Athletes</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Packages */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tight mb-4">
              Partnership Packages
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Flexible & Scalable Sponsorship Plans
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Silver Package */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Silver</span>
                  <h3 className="font-display font-bold text-4xl mt-2">$1,000</h3>
                  <p className="text-sm text-muted-foreground">Per Season</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">One Sponsor • One Athlete • One Season</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Primary Logo Placement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">12+ Event Representation</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full rounded-full" asChild>
                  <a href="mailto:ryan@elevatedarena.com?subject=Silver Partnership Inquiry">
                    Get Started
                  </a>
                </Button>
              </CardContent>
            </Card>
            
            {/* Gold Package */}
            <Card className="hover:shadow-xl transition-shadow border-primary/50 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold uppercase px-4 py-1 rounded-full">
                Popular
              </div>
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <span className="text-sm font-semibold uppercase tracking-wider text-primary">Gold</span>
                  <h3 className="font-display font-bold text-4xl mt-2">$2,000</h3>
                  <p className="text-sm text-muted-foreground">Per Season</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Athlete Sponsorship + Platform Feature</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Includes Silver Benefits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Featured Placement on Elevated Roping Media Platform</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Expanded Brand Visibility</span>
                  </li>
                </ul>
                <Button className="w-full rounded-full" asChild>
                  <a href="mailto:ryan@elevatedarena.com?subject=Gold Partnership Inquiry">
                    Get Started
                  </a>
                </Button>
              </CardContent>
            </Card>
            
            {/* Platinum Package */}
            <Card className="hover:shadow-xl transition-shadow bg-gradient-to-b from-amber-50 to-background dark:from-amber-950/20 dark:to-background border-amber-300/50">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <span className="text-sm font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">Platinum</span>
                  <h3 className="font-display font-bold text-4xl mt-2">$5,000</h3>
                  <p className="text-sm text-muted-foreground">Premier Partner</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Premier Platform Partner</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Athlete Sponsorship Included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Major Brand Placement Across Platform</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Featured in Launch & Media Campaigns</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full rounded-full border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30" asChild>
                  <a href="mailto:ryan@elevatedarena.com?subject=Platinum Partnership Inquiry">
                    Get Started
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-12 h-12 mx-auto mb-6 opacity-80" />
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase mb-4">
            Ready to Make a Difference?
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
            Contact us to learn more about sponsorship opportunities or to discuss custom partnership options 
            that align with your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="secondary"
              className="rounded-full px-10 py-6 font-bold uppercase tracking-wider"
              asChild
            >
              <a href="mailto:ryan@elevatedarena.com">
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="rounded-full px-10 py-6 font-bold uppercase tracking-wide border-2 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
              asChild
            >
              <Link to="/youth-athletes">
                Meet Our Athletes
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                alt="Elevated Roping Media" 
                className="h-8 w-auto"
              />
              <span className="font-display font-bold tracking-tight text-background/90">ELEVATED ROPING MEDIA</span>
            </Link>
            <p className="text-background/60 text-xs">
              © {new Date().getFullYear()} Elevated Roping Media Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SponsorshipPage;
